import React from 'react';
import type { ComponentProps, ReactNode } from 'react';

import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { expVal } from '@confluence/feature-experiments';
import { VIEW_PAGE, VIEW_BLOG } from '@confluence/named-routes/entry-points/routes.ts';
import { usePageState } from '@confluence/page-context';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import type { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';

import { useViewPageParams } from './useViewPageParams.ts';

type ContributorsRef = ComponentProps<typeof ObjectSidebarControl>['contributorsFragmentRef'];

type Props = {
	contentId: string;
	contentIdFromQuery?: string | null;
	contributorsFragmentRef?: ContributorsRef | null;
	isEmbeddedPage?: boolean;
	isLivePage?: boolean;
	children: (contributorsFragmentRef?: ContributorsRef) => ReactNode;
};

export const ViewPageContributors = ({
	contentId,
	contentIdFromQuery,
	contributorsFragmentRef,
	isEmbeddedPage,
	isLivePage,
	children,
}: Props): React.JSX.Element => {
	const [{ routeName }] = usePageState();
	const { versionOverride, embeddedContentRender } = useViewPageParams();
	if (
		contentId &&
		contentId === contentIdFromQuery &&
		contributorsFragmentRef &&
		!isEmbeddedPage &&
		!isLivePage &&
		!embeddedContentRender &&
		versionOverride == null &&
		(routeName === VIEW_PAGE.name || routeName === VIEW_BLOG.name) &&
		isRelayEnabled('@confluence/view-page-common') &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
		// Fail closed until the new dynamic kill switch is provisioned and explicitly disabled.
		!StatsigConfigurations.getBoolean('confluence_vp_contributors_frag_reader', true) &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return <>{children(contributorsFragmentRef)}</>;
	}
	return <>{children()}</>;
};
